import { booleanAttribute, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, ViewEncapsulation } from '@angular/core';

/**
 * Visual variants, mirroring the Angular Material button set. House style for a filled button
 * is `flat`; `raised` exists for the rare spot that really wants elevation.
 */
export type AraButtonVariant = 'basic' | 'raised' | 'stroked' | 'flat' | 'icon' | 'fab' | 'mini-fab';
/** Colour themes. */
export type AraButtonColor = 'primary' | 'secondary' | 'success' | 'warn' | 'error';
/**
 * Size step. Scales the box and the glyph together; override either axis on its own with the
 * `--ara-btn-size` / `--ara-btn-icon-size` custom properties (see the scss).
 *
 * | | box (icon-only) | text height | glyph |
 * |---|---|---|---|
 * | `xs` | 24px | 28px | 16px |
 * | `sm` | 32px | 32px | 18px |
 * | `md` | 40px | 36px | 20px |
 * | `lg` | 48px | 44px | 24px |
 */
export type AraButtonSize = 'xs' | 'sm' | 'md' | 'lg';
/** Outline shape of an icon-shaped button. Only meaningful together with `iconOnly` / the round variants. */
export type AraButtonShape = 'round' | 'square';

/**
 * araButton — a Material-look button whose styling we fully own (no MDC token fighting).
 *
 * Built like Angular Material's own button: an attribute-selector component on a native
 * `<button>` / `<a>`. The host stays a real button (so `type`, `disabled`, `(click)`, forms
 * and a11y are free); the component adds the class logic and a template we can extend later
 * (ripple layer, loading spinner, icon slots). Styling lives in the scoped `.scss` and is
 * tweakable there (CSS custom properties + per-variant rules).
 *
 * ```html
 * <button araButton>Basic</button>
 * <button araButton="icon"><mat-icon>edit</mat-icon></button>
 * <button araButton="flat" color="primary" [loading]="saving()">Save</button>
 * <button araButton="stroked" color="error"><mat-icon>delete</mat-icon> Delete</button>
 * <button araButton="icon" [active]="filterOn()"><mat-icon>filter_list</mat-icon></button>
 * <a araButton="flat" color="primary" [routerLink]="…">Link button</a>
 * ```
 *
 * `araButton` = variant (empty / omitted → `basic`). `color` **defaults to `secondary`** (neutral
 * ink/grey — the normal button colour), so a plain action button needs no `color` at all. Reserve
 * `color="primary"` for the one affirmative main action of a view/dialog (save, submit, create);
 * `error` for destructive actions, `success` / `warn` for the rare positive/caution cases.
 *
 * **Icon-only buttons.** `araButton="icon"` is the round transparent one. For a *bordered* or
 * *filled* icon button, combine any variant with `iconOnly` — the label slot collapses and the
 * host becomes a `size`-square box:
 *
 * ```html
 * <button araButton="stroked" iconOnly size="sm"><mat-icon>refresh</mat-icon></button>
 * <button araButton="flat" iconOnly><mat-icon>add</mat-icon></button>
 * ```
 *
 * `iconOnly` defaults to `shape="square"` (rounded rectangle), the round variants to
 * `shape="round"` — pass `shape` to swap either way.
 *
 * **Hover area vs. glyph.** `size` scales both. When only the click/hover target should grow (a
 * dense card header that must keep its 16px glyph), keep `size` and set `--ara-btn-size` alone.
 *
 * **Badge.** Binding `badge` at all shows a coloured circle at the top-right corner — with the
 * bound text/number inside, or as a plain dot when the value is empty. `badgeColor` picks the
 * circle's colour (default `error`).
 *
 * ```html
 * <button araButton="icon" [badge]="openCount()"><mat-icon>notifications</mat-icon></button>
 * <button araButton="icon" badge="" badgeColor="success"><mat-icon>cloud_done</mat-icon></button>
 * ```
 */
@Component({
	// An attribute component on a native button — the element part cannot carry the `ara` prefix.
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: 'button[araButton], a[araButton]',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	template: `
		@if (loading()) {
			<span class="ara-btn__spinner" aria-hidden="true"></span>
		}
		<span class="ara-btn__content"><ng-content></ng-content></span>
		@if (hasBadge()) {
			<span
				class="ara-btn__badge"
				[class.ara-btn__badge--dot]="isBadgeDot()"
				[class.ara-btn__badge--primary]="badgeColor() === 'primary'"
				[class.ara-btn__badge--secondary]="badgeColor() === 'secondary'"
				[class.ara-btn__badge--success]="badgeColor() === 'success'"
				[class.ara-btn__badge--warn]="badgeColor() === 'warn'"
				[class.ara-btn__badge--error]="badgeColor() === 'error'">
				{{ badgeText() }}
			</span>
		}
	`,
	styleUrl: './ara-button.component.scss',
	host: {
		class: 'ara-btn',
		'[attr.disabled]': '(disabled() || loading()) ? true : null',
		'[class.ara-btn--loading]': 'loading()',
		'[attr.aria-busy]': 'loading()',
		'[attr.aria-disabled]': '(disabled() || loading()) ? true : null',
		'[class.ara-btn--active]': 'isActive()',
		'[attr.aria-pressed]': 'isToggle() ? isActive() : null',
		'[class.ara-btn--basic]': "resolvedVariant() === 'basic'",
		'[class.ara-btn--raised]': "resolvedVariant() === 'raised'",
		'[class.ara-btn--stroked]': "resolvedVariant() === 'stroked'",
		'[class.ara-btn--flat]': "resolvedVariant() === 'flat'",
		'[class.ara-btn--icon]': "resolvedVariant() === 'icon'",
		'[class.ara-btn--fab]': "resolvedVariant() === 'fab'",
		'[class.ara-btn--mini-fab]': "resolvedVariant() === 'mini-fab'",
		'[class.ara-btn--icon-only]': 'iconOnly()',
		'[class.ara-btn--round]': "isIconShaped() && resolvedShape() === 'round'",
		'[class.ara-btn--square]': "isIconShaped() && resolvedShape() === 'square'",
		'[class.ara-btn--xs]': "size() === 'xs'",
		'[class.ara-btn--sm]': "size() === 'sm'",
		'[class.ara-btn--lg]': "size() === 'lg'",
		'[class.ara-btn--primary]': "color() === 'primary'",
		'[class.ara-btn--secondary]': "color() === 'secondary'",
		'[class.ara-btn--success]': "color() === 'success'",
		'[class.ara-btn--warn]': "color() === 'warn'",
		'[class.ara-btn--error]': "color() === 'error'",
	},
})
export class AraButtonComponent {
	/**
	 * The native `<button>` / `<a>` this sits on. Because araButton is a *component*, a bare
	 * template ref (`<button araButton #btn>`) resolves to this instance rather than to the element
	 * — so anything that needs the DOM node (an overlay origin, `scrollIntoView`, `focus`) reads
	 * `btn.hostElement`.
	 */
	readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

	/** Visual variant; empty string / omitted falls back to `basic`. */
	readonly araButton = input<AraButtonVariant | ''>('basic');
	/** Colour theme. Default `secondary` = neutral ink/grey (the normal look); `primary` = brand accent. */
	readonly color = input<AraButtonColor>('secondary');
	/** Disable the button (also settable via the native `disabled` attribute). */
	readonly disabled = input(false, { transform: booleanAttribute });
	/**
	 * Show a leading spinner and block interaction. The label stays visible; only a leading
	 * projected `<mat-icon>` is hidden, so the button keeps its width while it works.
	 */
	readonly loading = input(false, { transform: booleanAttribute });
	/**
	 * Pressed state for toggle-like buttons (filter on/off, editor formatting, lock). Leave it
	 * unset on normal buttons; binding it — even to `false` — opts into toggle semantics, so the
	 * host reports `aria-pressed` and the pressed tint follows the value. Prefer this over
	 * switching `color` on a boolean, and over the `.ara-force-active` showcase class.
	 */
	readonly active = input<boolean | undefined, boolean | string | undefined>(undefined, {
		transform: (value) => (value === undefined ? undefined : booleanAttribute(value)),
	});
	/** Size step (box + glyph). See {@link AraButtonSize} for the ladder. */
	readonly size = input<AraButtonSize>('md');
	/**
	 * Render as a square icon button: the 6rem label floor and the horizontal padding collapse and
	 * the host becomes a `size`-square box. Combines with any variant — `stroked iconOnly` is the
	 * bordered icon button, `flat iconOnly` the filled one.
	 */
	readonly iconOnly = input(false, { transform: booleanAttribute });
	/**
	 * Outline of an icon-shaped button. Defaults to `round` for the `icon`/`fab`/`mini-fab`
	 * variants and to `square` for `iconOnly`; ignored on text buttons.
	 */
	readonly shape = input<AraButtonShape | undefined>(undefined);
	/**
	 * Badge content shown in a coloured circle at the top-right corner. Binding it at all shows the
	 * circle — an empty value renders a plain dot. Leave unset for no badge.
	 */
	readonly badge = input<string | number | undefined>(undefined);
	/** Badge circle colour. */
	readonly badgeColor = input<AraButtonColor>('error');

	protected readonly resolvedVariant = computed<AraButtonVariant>(() => this.araButton() || 'basic');
	/** True once the consumer opted into toggle semantics by binding `active` at all. */
	protected readonly isToggle = computed(() => this.active() !== undefined);
	protected readonly isActive = computed(() => this.active() === true);
	/** Icon-shaped = the round variants or an explicit `iconOnly`; only then does `shape` apply. */
	protected readonly isIconShaped = computed(() => {
		const variant = this.resolvedVariant();
		return this.iconOnly() || variant === 'icon' || variant === 'fab' || variant === 'mini-fab';
	});
	protected readonly resolvedShape = computed<AraButtonShape>(() => {
		const shape = this.shape();
		if (shape) {
			return shape;
		}
		// The round variants stay round; `iconOnly` is the rounded-rectangle icon button.
		return this.iconOnly() && this.resolvedVariant() !== 'icon' ? 'square' : 'round';
	});
	/** True once the consumer opted into a badge by binding `badge` at all (empty value → dot). */
	protected readonly hasBadge = computed(() => this.badge() !== undefined && this.badge() !== null);
	protected readonly badgeText = computed(() => String(this.badge() ?? '').trim());
	protected readonly isBadgeDot = computed(() => !this.badgeText());
}
